import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Loader2, Mail } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pagina che gestisce sia /newsletter/conferma/:token (double opt-in)
 * sia /newsletter/disiscrivi/:token (unsubscribe GDPR).
 * L'azione viene determinata dalla prop `action` passata dalla route.
 */
export default function NewsletterActionPage({ action = "conferma" }) {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const endpoint = action === "disiscrivi"
      ? `${API}/newsletter/unsubscribe/${token}`
      : `${API}/newsletter/confirm/${token}`;
    axios
      .get(endpoint)
      .then((res) => {
        setStatus("ok");
        setMessage(res.data?.message || "Operazione completata.");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err?.response?.data?.detail || "Link non valido o scaduto.");
      });
  }, [token, action]);

  const isUnsub = action === "disiscrivi";
  const title = isUnsub ? "Disiscrizione newsletter" : "Conferma iscrizione";

  return (
    <div className="min-h-screen bg-[#faf8f5]" data-testid="newsletter-action-page">
      <Header />
      <main className="max-w-xl mx-auto px-4 py-20">
        <div className="bg-white rounded-2xl border border-zinc-200 p-8 md:p-12 shadow-sm text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-12 h-12 text-[#163321] mx-auto mb-4 animate-spin" />
              <h1 className="text-xl font-semibold text-zinc-900">In elaborazione...</h1>
            </>
          )}

          {status === "ok" && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                {isUnsub ? (
                  <Mail className="w-8 h-8 text-emerald-700" />
                ) : (
                  <CheckCircle2 className="w-8 h-8 text-emerald-700" />
                )}
              </div>
              <h1 className="text-2xl font-bold text-zinc-900 mb-3">{title} completata</h1>
              <p className="text-zinc-600 leading-relaxed">{message}</p>
              {!isUnsub && (
                <p className="text-sm text-zinc-500 mt-6">
                  Il prossimo digest arriverà <strong>venerdì alle 18:00</strong>.
                </p>
              )}
              <Link
                to="/"
                className="inline-block mt-8 px-6 py-3 rounded-lg bg-[#163321] text-white font-semibold hover:bg-[#0f2217] transition"
                data-testid="newsletter-action-home"
              >
                Torna alla home
              </Link>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="w-8 h-8 text-rose-700" />
              </div>
              <h1 className="text-2xl font-bold text-zinc-900 mb-3">Link non valido</h1>
              <p className="text-zinc-600 leading-relaxed">{message}</p>
              <Link
                to="/novita-noleggio"
                className="inline-block mt-8 px-6 py-3 rounded-lg bg-[#163321] text-white font-semibold hover:bg-[#0f2217] transition"
                data-testid="newsletter-action-retry"
              >
                Riprova iscrizione
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
